import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { Alert } from '@interfaces/models/alert';
import Sort from '@components/catalog/sort/sort';
import { AlertOrderType } from '@interfaces/models/alert';
import Button from '@components/atoms/button/button';
import { useAuthentication } from '@context/authentication.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import clsx from 'clsx';
import useUser from '@hooks/user/use-user';
import PageHeader from '@components/organisms/page-header/page-header';
import InfoPopover from '@components/atoms/info-popover/info-popover';
import useLocalStorage from '@hooks/use-local-storage';
import styles from './alert-header.module.scss';
interface AlertHeaderProps {
  isLanding: boolean;
  alertsList: Alert[];
  errorMessage?: string;
  loading?: boolean;
  isSortEnabled?: boolean;
  onSortChange?: (type: AlertOrderType) => void;
  sortBy?: AlertOrderType;
  onSortButtonClick?: (open: boolean) => void;
}

const ALERT_MANAGEMENT_URL = '/alerts/management';

const AlertHeader: React.FC<AlertHeaderProps> = (props) => {
  const {
    isLanding,
    alertsList,
    errorMessage = '',
    loading,
    isSortEnabled = false,
    onSortChange,
    sortBy,
    onSortButtonClick,
  } = props;
  const { t } = useTranslation();
  const { t: tSavedSearches } = useTranslation('saved-searches');
  const { setAuthOpen } = useAuthentication();
  const { sendAnalyticEvent: sendAlertAnalyticEvent } = useAnalyticEvents('alerts');
  const { isAuthenticated } = useUser();
  const [isSortModalOpen, setIsSortModalOpen] = React.useState(false);
  const showAlertSorting = isLanding && isSortEnabled && alertsList?.length > 1 && isAuthenticated;

  const [hasSeenSortTooltip, setHasSeenSortTooltip] = useLocalStorage<boolean>(
    'hasSeenAlertsSortTooltip',
    false,
    false,
  );
  const [showSortTooltip, setShowSortTooltip] = useState(false);

  useEffect(() => {
    if (!hasSeenSortTooltip && showAlertSorting) {
      setShowSortTooltip(true);
    }
  }, [hasSeenSortTooltip, showAlertSorting]);

  const dismissSortTooltip = () => {
    setShowSortTooltip(false);
    setHasSeenSortTooltip(true);
  };
  const breadCrumbLanding = [
    {
      localizedName: t('SITEMAP.BREADCRUMB.HOME'),
      url: {
        path: '/',
      },
    },
    {
      localizedName: t('ALERTS.LANDING.TITLE'),
      url: {
        path: '/alerts/',
      },
    },
  ];

  const breadCrumbs: Breadcrumb[] = isLanding
    ? breadCrumbLanding
    : [
        ...breadCrumbLanding,
        {
          localizedName: t('ALERTS.MANAGE_TEXT'),
          url: {
            path: ALERT_MANAGEMENT_URL,
          },
        },
      ];

  const authDialogHandler = (): void => {
    setAuthOpen(false);
  };

  const sendTracking = () => {
    sendAlertAnalyticEvent('alert_manage');
  };

  const showCTA = isLanding && isAuthenticated && alertsList?.length > 0;

  return (
    <>
      <div className={clsx(styles.alertHeader, 'innerContainer')}>
        <PageHeader
          title={isLanding ? t('ALERTS.LANDING.TITLE') : tSavedSearches('ALERTS.MANAGE.TITLE')}
          breadcrumbs={breadCrumbs}
          showDivider={true}
          dataCyPrefix="alerts"
          className={styles.alertHeader__pageHeader}
          isAlertHeader={true}
          cta={
            showCTA
              ? {
                  label: t('ALERTS.MANAGE_TEXT'),
                  href: ALERT_MANAGEMENT_URL,
                  onClick: sendTracking,
                  isLoading: loading,
                  dataCy: 'alerts-manage-link',
                  dataAttributes: {
                    'data-vc-dd-action-name': 'manageAlertsFromLanding',
                  },
                }
              : undefined
          }
        />

        {showAlertSorting && (
          <div className={styles.alertHeader__sortContainer}>
            <InfoPopover
              title=""
              subtitle={tSavedSearches('ALERTS.LANDING.SORT.TOOLTIP')}
              dataCy="alerts-sort-tooltip"
              className={styles.alertHeader__sortTooltip}
              isVisible={showSortTooltip}
              showCloseButton={false}
              closeOnScreenClick={true}
              showOnIntersection={false}
              position="bottom"
              arrowPosition="center"
              onClose={dismissSortTooltip}
            >
              <Sort
                onChange={(type) => onSortChange?.(type)}
                defaultValue={sortBy ?? 'newest_item'}
                openDialog={setIsSortModalOpen}
                open={isSortModalOpen}
                handleClickOnButton={onSortButtonClick}
                options={[
                  { text: tSavedSearches('ALERTS.LANDING.SORT.NEWEST_ITEMS'), value: 'newest_item' },
                  { text: tSavedSearches('ALERTS.LANDING.SORT.NEW_TO_OLD'), value: 'created_at_desc' },
                  { text: tSavedSearches('ALERTS.LANDING.SORT.OLD_TO_NEW'), value: 'created_at_asc' },
                ]}
                fullScreenOnMobile={false}
              />
            </InfoPopover>
          </div>
        )}
      </div>
      {!isAuthenticated && (
        <div className={clsx(styles.alertHeader__logoutText, 'vc-text-l innerContainer')}>
          <p>{t('ALERTS.LANDING.LOGOUT.TEXT')}</p>

          <Button
            variant="primary"
            size="medium"
            onClick={() => authDialogHandler()}
            data-cy="alerts-login-button"
          >
            {t('ALERTS.LANDING.LOGOUT.BUTTON')}
          </Button>
        </div>
      )}
      {alertsList && !alertsList.length && !errorMessage && !loading && isAuthenticated && (
        <div className="innerContainer">
          <p className={clsx(styles.alertHeader__emptyText, 'vc-text-l')}>{t('ALERTS.LANDING.EMPTY.TEXT')}</p>
          <p className={clsx(styles.alertHeader__emptySubtext, 'vc-text-l')}>{t('ALERTS.LANDING.EMPTY.SUBTEXT')}</p>
        </div>
      )}
    </>
  );
};

export default AlertHeader;
